import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaBriefcase, FaMoneyBillWave, FaMapMarkerAlt, FaFileUpload, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const Careers = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dob: '',
        experience: '',
        currentSalary: '',
        expectedSalary: '',
        availability: '',
        location: '',
        resume: null
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['image/jpeg', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const allowedExtensions = ['jpg', 'jpeg', 'pdf', 'doc', 'docx'];

            if (allowedExtensions.includes(fileExtension)) {
                setFormData(prev => ({ ...prev, resume: file }));
            } else {
                alert(t('careers.form.invalidFileType'));
                e.target.value = null;
                setFormData(prev => ({ ...prev, resume: null }));
            }
        }
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const formDataToSubmit = {
                ...formData,
                _subject: `New Job Application: ${formData.firstName} ${formData.lastName}`,
                _template: 'table',
                _captcha: 'false'
            };

            const response = await fetch("https://formsubmit.co/ajax/choundeshwaripatasanstha@gmail.com", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formDataToSubmit)
            });

            if (response.ok) {
                setSubmitStatus('success');
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    dob: '',
                    experience: '',
                    currentSalary: '',
                    expectedSalary: '',
                    availability: '',
                    location: '',
                    resume: null
                });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const locations = [
        { key: 'palus', label: t('careers.locations.palus') },
        { key: 'vita', label: t('careers.locations.vita') }
    ];

    return (
        <div className="pt-20 pb-20 bg-gray-50/50 min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Hero section for Careers */}
                <div className="text-center mb-16" data-aos="fade-up">
                    <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-3 block">
                        {t('careers.subtitle')}
                    </span>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-primary-dark mb-6">
                        {t('careers.title')}
                    </h1>
                    <div className="w-24 h-1.5 bg-secondary mx-auto mb-8 rounded-full"></div>
                    <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
                        {t('careers.description')}
                    </p>
                </div>

                <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100" data-aos="fade-up">
                    <div className="p-8 md:p-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-10 border-b pb-4 border-gray-100 flex items-center gap-3">
                            <FaBriefcase className="text-primary" /> {t('careers.form.title')}
                        </h2>

                        <form onSubmit={handleFormSubmit} className="max-w-4xl mx-auto space-y-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                                {/* First Name */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.firstName')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.lastName')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Email Address */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.email')} *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Mobile Number */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.phone')} *
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        required
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Date of Birth */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.dob')} *
                                    </label>
                                    <input
                                        type="date"
                                        name="dob"
                                        required
                                        value={formData.dob}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Experience */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.experience')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="experience"
                                        required
                                        value={formData.experience}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Current Salary */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.currentSalary')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="currentSalary"
                                        required
                                        value={formData.currentSalary}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Expected Salary */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.expectedSalary')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="expectedSalary"
                                        required
                                        value={formData.expectedSalary}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Available to Join */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.availability')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="availability"
                                        required
                                        value={formData.availability}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                                    />
                                </div>

                                {/* Preferred Location */}
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                        {t('careers.form.location')} *
                                    </label>
                                    <select
                                        name="location"
                                        required
                                        value={formData.location}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 bg-gray-100 border-none rounded-lg focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none cursor-pointer"
                                    >
                                        <option value="">{t('careers.form.selectLocation')}</option>
                                        {locations.map((loc, idx) => (
                                            <option key={idx} value={loc.key}>{loc.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* File Upload Section */}
                            <div className="bg-gray-100 p-8 rounded-xl border-2 border-dashed border-gray-200">
                                <div className="flex flex-col md:flex-row items-center gap-6">
                                    <label className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded cursor-pointer transition-colors font-bold uppercase tracking-wide shadow-md">
                                        {t('careers.form.resume')}
                                        <input type="file" className="hidden" onChange={handleFileChange} accept=".jpg,.jpeg,.pdf,.doc,.docx" />
                                    </label>
                                    <span className="text-gray-500 font-medium">
                                        {formData.resume ? formData.resume.name : t('careers.form.noFileChosen')}
                                    </span>
                                </div>
                                <p className="mt-4 text-gray-400 text-sm">
                                    {t('careers.form.resumeHint')}
                                </p>
                            </div>

                            {/* Privacy Consent */}
                            <div className="flex items-start gap-4">
                                <input type="checkbox" required className="mt-1.5 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary" />
                                <p className="text-gray-600 text-sm leading-relaxed font-medium">
                                    {t('careers.form.privacyConsent')}
                                </p>
                            </div>

                            {/* Status Messages */}
                            {submitStatus === 'success' && (
                                <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 animate-fade-in">
                                    <FaCheckCircle /> {t('careers.form.success')}
                                </div>
                            )}
                            {submitStatus === 'error' && (
                                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 animate-fade-in">
                                    <FaTimesCircle /> {t('careers.form.error')}
                                </div>
                            )}

                            {/* Submit Button */}
                            <div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`bg-primary hover:bg-primary-dark text-white px-10 py-4 rounded-lg font-bold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center gap-3 uppercase tracking-wider ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('careers.form.applying')}
                                        </>
                                    ) : (
                                        <>
                                            {t('careers.form.submit')} <FaPaperPlane className="text-sm rotate-45" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Careers;
